import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { formatDate, formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import { CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ArrowsLeftRightIcon as ArrowsLeftRight } from "@phosphor-icons/react";

const TABS = [
  { id: "pending_admin_approve", label: "Pending review" },
  { id: "pending_buyer_accept", label: "Awaiting buyer" },
  { id: "pending_seller_accept", label: "Awaiting owner" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function AdminTransfers() {
  const [status, setStatus] = useState("pending_admin_approve");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = (s = status) => {
    setLoading(true);
    api
      .get(`/admin/transfers?status=${s}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const decide = async (id, kind) => {
    let reason = "";
    if (kind === "reject") {
      reason = window.prompt("Reason for rejection:", "") || "";
    }
    setBusy(id);
    try {
      await api.post(`/admin/transfers/${id}/${kind}`, { reason });
      toast.success(kind === "approve" ? "Transfer approved" : "Transfer rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="admin-transfers-heading">
            Transfer approvals
          </h1>
          <p className="text-[#6B6B6B] mt-2">
            Review, approve, or reject ownership transfers. Approving moves the
            dog's registration to the new owner and appends an immutable entry
            to its ownership history.
          </p>
        </div>

        <Tabs value={status} onValueChange={setStatus}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white border border-[#E5E5E5] rounded-full p-1">
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="rounded-full px-4 text-xs"
                  data-testid={`admin-transfers-tab-${t.id}`}
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {TABS.map((t) => (
            <TabsContent key={t.id} value={t.id}>
              {loading ? (
                <div className="text-[#6B6B6B] mt-4">Loading…</div>
              ) : rows.length === 0 ? (
                <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center mt-4" data-testid="admin-transfers-empty">
                  <ArrowsLeftRight size={40} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
                  <p className="font-spectral text-xl">No transfers in this tab</p>
                </div>
              ) : (
                <div className="space-y-3 mt-4" data-testid="admin-transfers-list">
                  {rows.map((t) => (
                    <div
                      key={t.id}
                      className="bg-white border border-[#EAEAEA] rounded-xl p-5"
                      data-testid={`admin-transfer-row-${t.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link
                              to={`/dogs/${t.dog_id}`}
                              className="font-spectral text-xl tracking-tight hover:text-crimson transition-colors"
                            >
                              {t.dog?.name || "Unknown dog"}
                            </Link>
                            {t.dog?.registration_number && (
                              <span className="text-xs font-mono text-[#6B6B6B]">
                                {t.dog.registration_number}
                              </span>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {t.initiated_by === "seller" ? "Seller initiated" : "Buyer claimed"}
                            </Badge>
                          </div>
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                                From (current owner)
                              </p>
                              <p className="mt-1">
                                {t.from_user?.name || "—"}{" "}
                                <span className="text-[#6B6B6B]">
                                  ({t.from_user?.email || "—"})
                                </span>
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                                To (new owner)
                              </p>
                              <p className="mt-1">
                                {t.to_user?.name || t.to_name || "—"}{" "}
                                <span className="text-[#6B6B6B]">
                                  ({t.to_email || "—"})
                                </span>
                                {!t.to_user_id && (
                                  <span className="ml-2 inline-flex items-center text-xs text-[#8A6A00] bg-[#FBF7EC] border border-[#C9A227] px-2 py-0.5 rounded-full">
                                    No account yet
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          {t.message && (
                            <p className="text-sm text-[#555] mt-3 italic bg-[#FAFAFA] border-l-2 border-[#C9A227] pl-3 py-1">
                              "{t.message}"
                            </p>
                          )}
                          <p className="text-xs text-[#8B8B8B] mt-3">
                            Created {formatDate(t.created_at)}
                            {t.accepted_at ? ` · Accepted ${formatDate(t.accepted_at)}` : ""}
                            {t.approved_at ? ` · Approved ${formatDate(t.approved_at)}` : ""}
                            {t.rejected_at ? ` · Rejected ${formatDate(t.rejected_at)}` : ""}
                          </p>
                          {t.rejection_reason && (
                            <p className="text-sm text-[#A12125] mt-1">
                              Reason: {t.rejection_reason}
                            </p>
                          )}
                        </div>
                        {t.status === "pending_admin_approve" && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              disabled={busy === t.id}
                              onClick={() => decide(t.id, "approve")}
                              className="rounded-full bg-ink hover:bg-crimson text-white"
                              data-testid={`admin-transfer-approve-${t.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" weight="duotone" />
                              Approve
                            </Button>
                            <Button
                              disabled={busy === t.id}
                              onClick={() => decide(t.id, "reject")}
                              variant="outline"
                              className="rounded-full border-[#E5E5E5] text-crimson"
                              data-testid={`admin-transfer-reject-${t.id}`}
                            >
                              <XCircle size={14} className="mr-1" weight="duotone" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AppShell>
  );
}
